/**
 * Unified-chooser flow — `?flow=unified`.
 *
 * Single entry point that surfaces every enabled login method as a
 * stacked button list. The partner SDK uses this when it wants ONE
 * "Sign in" button on its side that opens a modal where the end-user
 * picks how to authenticate — Privy / Magic / Clerk style.
 *
 * URL contract:
 *
 *   ?flow=unified
 *   &methods=google,email_password,email_link,phone    (optional — defaults to all 4)
 *   &origin=<partner origin>                            (validated, required)
 *   &state=<correlation>                                (echoed in postMessage)
 *   &appKey=<app_*>                                     (white-label policy)
 *   &b_*                                                (branding pass-through)
 *
 * When a user clicks one of the method buttons we navigate the same
 * window (popup OR iframe) to the corresponding `?flow=<method>` URL.
 * This is a same-origin navigation — the postMessage origin doesn't
 * change, the branding URL params come along for the ride, and the
 * SDK's listener on the parent doesn't need to know we did a hop. The
 * end-user sees a clean "pick a method → complete that method"
 * transition with no flash, no tab-switch.
 *
 * We don't compose the underlying form renderers inline because each
 * flow has subtle differences (post-link redirect for email-link,
 * reCAPTCHA setup for phone, etc.) — keeping them as full flow pages
 * is the lower-risk path. A future iteration can refactor each flow
 * to expose a "mount inside a container" function and stitch them
 * into a single-page experience.
 */

import { PostMessageContext } from '../postmessage';
import { el, mount, postCancel, getQueryParam } from '../util';
import { HorusUnifiedMethod } from '../protocol';

const ALL_METHODS: HorusUnifiedMethod[] = [
  'google',
  'email_password',
  'email_link',
  'phone',
];

/**
 * Display data per method. Unicode glyphs only — no inline SVG, no
 * raster icons, no third-party CDN. Keeps the auth-page CSP minimal
 * and eliminates the innerHTML XSS surface that an icon-set library
 * would introduce.
 */
const METHOD_DISPLAY: Record<HorusUnifiedMethod, { label: string; glyph: string }> = {
  google: { label: 'Continue with Google', glyph: 'G' },
  email_password: { label: 'Continue with email + password', glyph: '✉' },
  email_link: { label: 'Email me a sign-in link', glyph: '🔗' },
  phone: { label: 'Continue with phone', glyph: '📱' },
};

export function renderUnifiedFlow(ctx: PostMessageContext): void {
  const methods = parseMethodsParam();

  const title = el('h1', {}, ['Sign in']);
  const subtitle = el('p', { class: 'subtitle' }, [
    'Choose how you want to continue.',
  ]);

  const buttons = methods.map((m) => makeMethodButton(m));
  const cancel = el('button', { type: 'button', class: 'ghost' }, ['Cancel']);
  cancel.addEventListener('click', () => postCancel(ctx));

  mount(
    el('div', {}, [
      title,
      subtitle,
      el(
        'div',
        { style: 'display:flex;flex-direction:column;gap:8px;margin:16px 0;' },
        buttons
      ),
      cancel,
    ])
  );
}

/**
 * Reads `&methods=` URL param. Accepts comma-separated method names;
 * unknown values are dropped silently. Empty / missing → defaults to
 * all four methods.
 */
function parseMethodsParam(): HorusUnifiedMethod[] {
  const raw = getQueryParam('methods');
  if (!raw) return ALL_METHODS;
  const requested = raw
    .split(',')
    .map((s) => s.trim().toLowerCase()) as HorusUnifiedMethod[];
  const filtered = requested.filter((m) => ALL_METHODS.includes(m));
  // If everything got filtered out (typo), fall back to all rather than
  // showing an empty chooser the user can't escape from.
  return filtered.length > 0 ? filtered : ALL_METHODS;
}

function makeMethodButton(method: HorusUnifiedMethod): HTMLElement {
  const { label, glyph } = METHOD_DISPLAY[method];
  const btn = el(
    'button',
    {
      type: 'button',
      class: 'primary',
      'data-method': method,
      'data-testid': `unified-pick-${method}`,
      style:
        'display:flex;align-items:center;gap:12px;justify-content:flex-start;padding:12px 16px;',
    },
    [
      // textContent-driven glyph — safe by construction. Each is a
      // single unicode codepoint (or a short string for Google's "G").
      el(
        'span',
        {
          style:
            'display:inline-flex;align-items:center;justify-content:center;width:24px;font-weight:700;',
          'aria-hidden': 'true',
        },
        [glyph]
      ),
      el('span', {}, [label]),
    ]
  ) as HTMLButtonElement;

  btn.addEventListener('click', () => navigateToFlow(method));
  return btn;
}

/**
 * Same-origin nav from `?flow=unified` to `?flow=<method>`. All other
 * URL params (origin, state, appKey, branding, mode) propagate
 * automatically because we mutate `searchParams.flow` only.
 *
 * The browser navigates the popup / iframe to the new URL; main.ts
 * re-dispatches; the method's own flow file takes over. From the
 * SDK's perspective the postMessage listener is unchanged — same
 * origin, same state token, just a different `body.type` whenever
 * the inner flow completes.
 *
 * We also tag the URL with `autostart=1`. The user just made a
 * deliberate choice ("Continue with Google" etc.) — they don't
 * need to confirm it again on the next page. Each flow checks the
 * param on mount and, when set, fires its primary action directly
 * (Google → signInWithPopup; phone → focus the input; etc.)
 * instead of waiting for a second click on a "yes really sign me
 * in" button. Without this the inline-modal experience would be:
 * click Google in chooser → see another button labelled Continue
 * with Google → click it → finally see Google's OAuth picker. Two
 * unnecessary clicks per sign-in.
 */
function navigateToFlow(method: HorusUnifiedMethod): void {
  const url = new URL(window.location.href);
  url.searchParams.set('flow', method);
  url.searchParams.set('autostart', '1');
  window.location.assign(url.toString());
}
